import axios from "axios";
import { auth } from "../firebase.config";

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/v2/mealplan";

// Create axios instance
const api = axios.create({
    baseURL: `${API_BASE_URL}/api/v1`,
    headers: {
        "Content-Type": "application/json",
    },
});

// Add auth interceptor to include Firebase token
api.interceptors.request.use(
    async (config) => {
        // Get the Firebase auth token
        if (auth.currentUser) {
            try {
                const token = await auth.currentUser.getIdToken();
                config.headers.Authorization = `Bearer ${token}`;
            } catch (error) {
                console.error("Error getting Firebase token:", error);
            }
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Types for API configuration
export interface ApiConfig {
    model?: string;
    refinementmodel?: string;
    datasourceconfig: Record<string, any>;
}

// Types for user data
export interface User {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    phone_number?: string;
    date_of_birth?: string;
    firebase_uid?: string;
    is_active: boolean;
    is_verified: boolean;
    email_verified_at?: string;
    subscription_status: "free" | "premium" | "trial" | "cancelled";
    stripe_customer_id?: string;
    subscription_ends_at?: string;
    apiconfig?: ApiConfig;
    passiocustomerid?: string;
    passioapikey?: string;
    created_at: string;
    updated_at: string;
    last_login_at?: string;
    full_name: string;
    is_premium: boolean;
    // Payment method and free trial fields
    has_valid_payment_method: boolean;
    has_used_free_trial: boolean;
    free_runs_remaining: number;
    can_use_service: boolean;
}

// Types for profile data
export interface ProfileData {
    age?: number;
    gender?: "male" | "female" | "non_binary" | "prefer_not_to_say";
    height?: number;
    weight?: number;
    allergies: string[];
    dietary_restrictions: string[];
    likes?: string;
    dislikes?: string;
    notes?: string;
    [key: string]: any; // Allow extra fields
}

export interface Profile {
    id: number;
    user_id: number;
    name: string;
    profile_data: ProfileData;
    created_at: string;
    updated_at: string;
    // Extracted fields for convenience
    age?: number;
    gender?: string;
    height?: number;
    weight?: number;
    allergies: string[];
    dietary_restrictions: string[];
    likes?: string;
    dislikes?: string;
    notes?: string;
}

export interface CreateProfileData {
    name: string;
    profile_data: ProfileData;
}

export interface UpdateProfileData {
    name?: string;
    profile_data?: ProfileData;
}

export interface CreateUserData {
    email: string;
    first_name: string;
    last_name: string;
    password?: string;
    firebase_uid?: string;
    phone_number?: string;
    date_of_birth?: string;
}

export interface UpdateUserData {
    first_name?: string;
    last_name?: string;
    phone_number?: string;
    date_of_birth?: string;
    apiconfig?: ApiConfig;
}

export interface UpdateUserPassioData {
    passiocustomerid?: string;
    passioapikey?: string;
}

// Types for meal plan data
export interface MealPlanData {
    [key: string]: any; // Flexible JSON structure for meal plan content
}

export interface MealPlan {
    id: number;
    user_id: number;
    name: string;
    profiles: number[]; // Array of profile IDs
    plan: MealPlanData; // Free-form meal plan data
    generation_prompt?: string; // The prompt used to generate this meal plan
    generation_url?: string; // The API URL with query params used for generation
    token_count?: number; // Number of tokens used to generate this meal plan
    created_at: string;
    updated_at: string;
}

export interface CreateMealPlanData {
    name: string;
    profiles: number[]; // Array of profile IDs
    plan: MealPlanData; // Free-form meal plan data
    generation_prompt?: string; // The prompt used to generate this meal plan
    generation_url?: string; // The API URL with query params used for generation
    token_count?: number; // Number of tokens used to generate this meal plan
}

export interface UpdateMealPlanData {
    name?: string;
    profiles?: number[]; // Array of profile IDs
    plan?: MealPlanData; // Free-form meal plan data
    generation_prompt?: string;
    generation_url?: string;
    token_count?: number;
}

export interface DeleteMealPlansResponse {
    message: string;
    deleted_count: number;
}

// Pagination types
export interface PaginatedResponse<T> {
    items: T[];
    total: number;
    page: number;
    page_size: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
}

// API functions
export const userApi = {
    // Create user (signup)
    createUser: async (userData: CreateUserData): Promise<User> => {
        const response = await api.post("/users/signup", userData);
        return response.data;
    },

    // Get user by ID
    getUserById: async (userId: number): Promise<User> => {
        const response = await api.get(`/users/${userId}`);
        return response.data;
    },

    // Get user by email
    getUserByEmail: async (email: string): Promise<User> => {
        const response = await api.get(`/users/email/${email}`);
        return response.data;
    },

    // Get user by Firebase UID
    getUserByFirebaseUid: async (firebaseUid: string): Promise<User> => {
        const response = await api.get(`/users/firebase/${firebaseUid}`);
        return response.data;
    },

    // Update user
    updateUser: async (
        userId: number,
        userData: UpdateUserData
    ): Promise<User> => {
        const response = await api.put(`/users/${userId}`, userData);
        return response.data;
    },

    // Update last login
    updateLastLogin: async (userId: number): Promise<User> => {
        const response = await api.post(`/users/${userId}/login`);
        return response.data;
    },

    // Verify email with token
    verifyEmailWithToken: async (token: string): Promise<User> => {
        const response = await api.post(`/users/verify-email?token=${token}`);
        return response.data;
    },
    
    // Resend verification email
    resendVerificationEmail: async (email: string): Promise<{ message: string }> => {
        const response = await api.post("/users/resend-verification", { email });
        return response.data;
    },

    // Update API configuration
    updateApiConfig: async (
        userId: number,
        apiConfig: ApiConfig
    ): Promise<User> => {
        const response = await api.put(`/users/${userId}/apiconfig`, apiConfig);
        return response.data;
    },

    // Update Passio configuration
    updatePassioConfig: async (
        userId: number,
        passioData: UpdateUserPassioData
    ): Promise<User> => {
        const response = await api.put(`/users/${userId}/passio`, passioData);
        return response.data;
    },
    
    // Sync verification status with Firebase
    syncVerificationStatus: async (userId: number): Promise<User> => {
        const response = await api.post(`/users/${userId}/sync-verification-status`);
        return response.data;
    },
    
    // Request password reset
    requestPasswordReset: async (email: string): Promise<{ message: string }> => {
        const response = await api.post("/users/request-password-reset", { email });
        return response.data;
    },
    
    // Reset password with token
    resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
        const response = await api.post("/users/reset-password", {
            token,
            new_password: newPassword
        });
        return response.data;
    },
    
    // Activate free trial for user
    activateFreeTrial: async (userId: number): Promise<User> => {
        const response = await api.post(`/users/${userId}/activate-free-trial`);
        return response.data;
    },
    
    // Decrement free run for user
    decrementFreeRun: async (userId: number): Promise<User> => {
        const response = await api.post(`/users/${userId}/decrement-free-run`);
        return response.data;
    },
    
    // Check user access status
    checkUserAccess: async (userId: number): Promise<{
        has_access: boolean;
        free_runs_remaining: number;
        has_valid_payment_method: boolean;
        has_used_free_trial: boolean;
        is_premium: boolean;
        is_trial: boolean;
        subscription_status: string;
    }> => {
        const response = await api.get(`/users/${userId}/check-access`);
        return response.data;
    },
};

// Profile API functions
export const profileApi = {
    // Get all profiles for a user
    getUserProfiles: async (
        userId: number,
        options?: {
            page?: number;
            page_size?: number;
            sort_by?: string;
            sort_direction?: "asc" | "desc";
        }
    ): Promise<PaginatedResponse<Profile>> => {
        const params = new URLSearchParams();
        if (options?.page !== undefined)
            params.append("page", options.page.toString());
        if (options?.page_size !== undefined)
            params.append("page_size", options.page_size.toString());
        if (options?.sort_by) params.append("sort_by", options.sort_by);
        if (options?.sort_direction)
            params.append("sort_direction", options.sort_direction);

        const queryString = params.toString();
        const url = `/users/${userId}/profiles${
            queryString ? `?${queryString}` : ""
        }`;
        const response = await api.get(url);
        return response.data;
    },

    // Get user's default profile
    getUserDefaultProfile: async (userId: number): Promise<Profile> => {
        const response = await api.get(`/users/${userId}/profiles/default`);
        return response.data;
    },

    // Get specific profile
    getProfile: async (userId: number, profileId: number): Promise<Profile> => {
        const response = await api.get(
            `/users/${userId}/profiles/${profileId}`
        );
        return response.data;
    },

    // Create new profile
    createProfile: async (
        userId: number,
        profileData: CreateProfileData
    ): Promise<Profile> => {
        const response = await api.post(
            `/users/${userId}/profiles`,
            profileData
        );
        return response.data;
    },

    // Update profile
    updateProfile: async (
        userId: number,
        profileId: number,
        profileData: UpdateProfileData
    ): Promise<Profile> => {
        const response = await api.put(
            `/users/${userId}/profiles/${profileId}`,
            profileData
        );
        return response.data;
    },

    // Update profile data only
    updateProfileData: async (
        userId: number,
        profileId: number,
        profileData: ProfileData
    ): Promise<Profile> => {
        const response = await api.put(
            `/users/${userId}/profiles/${profileId}/data`,
            profileData
        );
        return response.data;
    },

    // Delete profile
    deleteProfile: async (userId: number, profileId: number): Promise<void> => {
        await api.delete(`/users/${userId}/profiles/${profileId}`);
    },

    // Delete all user profiles
    deleteAllUserProfiles: async (
        userId: number
    ): Promise<{ message: string }> => {
        const response = await api.delete(`/users/${userId}/profiles`);
        return response.data;
    },
};

// Meal Plan API functions
export const mealPlanApi = {
    // Get all meal plans for a user
    getUserMealPlans: async (
        userId: number,
        options?: {
            page?: number;
            page_size?: number;
            sort_by?: string;
            sort_direction?: "asc" | "desc";
        }
    ): Promise<PaginatedResponse<MealPlan>> => {
        const params = new URLSearchParams();
        if (options?.page !== undefined)
            params.append("page", options.page.toString());
        if (options?.page_size !== undefined)
            params.append("page_size", options.page_size.toString());
        if (options?.sort_by) params.append("sort_by", options.sort_by);
        if (options?.sort_direction)
            params.append("sort_direction", options.sort_direction);

        const queryString = params.toString();
        const url = `/users/${userId}/mealplans${
            queryString ? `?${queryString}` : ""
        }`;
        const response = await api.get(url);
        return response.data;
    },

    // Create a new meal plan for a user
    createMealPlan: async (
        userId: number,
        mealPlanData: CreateMealPlanData
    ): Promise<MealPlan> => {
        const response = await api.post(
            `/users/${userId}/mealplans`,
            mealPlanData
        );
        return response.data;
    },

    // Get a specific meal plan for a user
    getMealPlan: async (
        userId: number,
        mealPlanId: number
    ): Promise<MealPlan> => {
        const response = await api.get(
            `/users/${userId}/mealplans/${mealPlanId}`
        );
        return response.data;
    },

    // Update a specific meal plan for a user
    updateMealPlan: async (
        userId: number,
        mealPlanId: number,
        mealPlanData: UpdateMealPlanData
    ): Promise<MealPlan> => {
        const response = await api.put(
            `/users/${userId}/mealplans/${mealPlanId}`,
            mealPlanData
        );
        return response.data;
    },

    // Update only the plan data (JSON field) for a specific meal plan
    updateMealPlanData: async (
        userId: number,
        mealPlanId: number,
        planData: MealPlanData
    ): Promise<MealPlan> => {
        const response = await api.put(
            `/users/${userId}/mealplans/${mealPlanId}/plan`,
            planData
        );
        return response.data;
    },

    // Delete a specific meal plan for a user
    deleteMealPlan: async (
        userId: number,
        mealPlanId: number
    ): Promise<{ message: string }> => {
        const response = await api.delete(
            `/users/${userId}/mealplans/${mealPlanId}`
        );
        return response.data;
    },

    // Delete all meal plans for a user
    deleteAllUserMealPlans: async (
        userId: number
    ): Promise<DeleteMealPlansResponse> => {
        const response = await api.delete(`/users/${userId}/mealplans`);
        return response.data;
    },

    // Search meal plans by name for a user
    searchMealPlans: async (
        userId: number,
        searchName: string,
        options?: {
            page?: number;
            page_size?: number;
        }
    ): Promise<PaginatedResponse<MealPlan>> => {
        const params = new URLSearchParams();
        params.append("name", searchName);
        if (options?.page !== undefined)
            params.append("page", options.page.toString());
        if (options?.page_size !== undefined)
            params.append("page_size", options.page_size.toString());

        const response = await api.get(
            `/users/${userId}/mealplans/search?${params.toString()}`
        );
        return response.data;
    },
};

// Subscription API functions
export const subscriptionApi = {
    // Create a setup intent for collecting payment method
    createSetupIntent: async (): Promise<{
        client_secret: string;
        publishable_key: string;
    }> => {
        const response = await api.post("/subscriptions/setup-intent");
        return response.data;
    },

    // Create a subscription
    createSubscription: async (): Promise<{
        subscription_id: string;
        status: string;
        current_period_end: number;
        passio_customer_id?: string;
        passio_api_key?: string;
        passio_subscription_created?: boolean;
        note?: string;
    }> => {
        const response = await api.post("/subscriptions/subscribe");
        return response.data;
    },

    // Create a checkout session (alternative to setup intent flow)
    createCheckoutSession: async (
        successUrl?: string,
        cancelUrl?: string
    ): Promise<{ checkout_url: string; session_id: string }> => {
        const response = await api.post(
            "/subscriptions/checkout-session",
            null,
            {
                params: {
                    success_url: successUrl,
                    cancel_url: cancelUrl,
                },
            }
        );
        return response.data;
    },

    // Get current subscription status
    getSubscriptionStatus: async (): Promise<{
        has_subscription: boolean;
        subscription_status: string;
        subscription_id?: string;
        current_period_end?: number;
        cancel_at_period_end?: boolean;
        passio_api_key?: string;
        passio_customer_id?: string;
        usage_data?: any;
    }> => {
        const response = await api.get("/subscriptions/subscription");
        return response.data;
    },

    // Cancel subscription
    cancelSubscription: async (
        immediately: boolean = false
    ): Promise<{
        subscription_id: string;
        status: string;
        cancel_at_period_end: boolean;
        canceled_at?: number;
    }> => {
        const response = await api.post("/subscriptions/cancel", null, {
            params: { immediately },
        });
        return response.data;
    },

    // Retrieve or generate API key for subscribed users
    retrieveApiKey: async (): Promise<{
        status: string;
        passio_api_key?: string;
        passio_customer_id?: string;
    }> => {
        const response = await api.post("/subscriptions/retrieve-api-key");
        return response.data;
    },

    // Update refill settings for subscription
    updateRefillSettings: async (
        refillBehaviour: "auto" | "off",
        refillBatchQuantity: number
    ): Promise<{
        success: boolean;
        subscription_id: string;
        refill_behaviour: string;
        refill_batch_quantity: number;
    }> => {
        const response = await api.put("/subscriptions/refill-settings", null, {
            params: {
                refill_behaviour: refillBehaviour,
                refill_batch_quantity: refillBatchQuantity,
            },
        });
        return response.data;
    },
};

// Task Worker API
export const taskWorkerApi = {
    createTaskWorkerEvent: async (requestData: any): Promise<{ id: string; status: string }> => {
        const response = await api.post(`/taskworker/events`, requestData);
        return response.data;
    },

    getTaskWorkerEvent: async (eventId: string): Promise<any> => {
        const response = await api.get(`/taskworker/events/${eventId}`);
        return response.data;
    },

    getUserTaskWorkerEvents: async (params?: { skip?: number; limit?: number; status?: string }): Promise<any[]> => {
        const response = await api.get(`/taskworker/events`, { params });
        return response.data;
    },
};

// Export a convenient apiService object with all API functions
export const apiService = {
    users: userApi,
    profiles: profileApi,
    mealplans: mealPlanApi,
    subscriptions: subscriptionApi,
    taskWorker: taskWorkerApi,
};

export default api;
